"use client";

import { ComponentProps, ReactNode, type MouseEvent } from "react";
import { Button } from "@zapier/design-system";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import isBrowser from "src/utils/isBrowser";
import {
  type ReadonlyURLSearchParams,
  usePathname,
  useSearchParams,
} from "next/navigation";
import { useLocalStorage } from "src/hooks/useLocalStorage";
import { LOCALSTORAGE_TEMPLATE_CREATE_KEY } from "src/constants";
import { useFeatureFlag } from "../FeatureFlags";
import { useCurrentAccountId } from "@zapier/identity";
import { GoogleLogoSvg } from "./GoogleLogoSvg";
import styles from "./GoogleSSOButton.module.css";

const createGoogleSSOUrl = ({
  baseUrl,
  searchParams,
  pathname,
}: {
  baseUrl: string;
  searchParams: ReadonlyURLSearchParams | null;
  pathname: string | null;
}) => {
  if (!pathname) return;

  const parsedUrl = new URL("/google-sso/start", baseUrl);
  parsedUrl.searchParams.set("scopes", "basic");

  let nextParam = pathname;

  if (searchParams?.toString()) {
    nextParam += `?${searchParams.toString()}&create=true`;
  } else {
    nextParam += "?create=true";
  }

  parsedUrl.searchParams.set("next", nextParam);

  return parsedUrl.toString();
};

type GoogleSSOButtonProps = {
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
  slug: string;
};

export const GoogleSSOButton = ({
  onClick,
  children,
  slug,
  ...rest
}: GoogleSSOButtonProps & ComponentProps<typeof Button>) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentAccountId = useCurrentAccountId();
  const isTemplatesGoogleSSOEnabled = useFeatureFlag("templates_google_sso");

  const { setValue: setLocalStorageTemplateCreateKey } =
    useLocalStorage<string>(LOCALSTORAGE_TEMPLATE_CREATE_KEY);

  const shouldRender =
    !currentAccountId &&
    (isTemplatesGoogleSSOEnabled ||
      searchParams?.get("templates_google_sso") === "true"); // Workaround for testing in logged out pages

  const ssoStartUrl = createGoogleSSOUrl({
    baseUrl: isBrowser()
      ? window.location.origin
      : process.env.NEXT_PUBLIC_TEMPLATES_BASE_URL!,
    searchParams,
    pathname,
  });

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    emitUserInteractionEvent(UserInteractionEventName.googleSSOButtonClicked, {
      event_action: "click",
      option_selected: "",
    });

    setLocalStorageTemplateCreateKey(slug);

    if (onClick) {
      onClick(event);
    }

    if (ssoStartUrl) {
      window.location.assign(ssoStartUrl.toString());
    }
  };

  if (!ssoStartUrl || !shouldRender) return null;

  return (
    // TODO: replace this with the beta component once presentationOnly prop is supported
    <Button
      href={ssoStartUrl.toString()}
      onClick={handleClick}
      color="tertiary"
      {...rest}
    >
      <span className={styles.buttonContent}>
        <GoogleLogoSvg />
        {children}
      </span>
    </Button>
  );
};
